import { BuildingDesign } from '@shared/models/design/building-design.model';
import { Coordinate } from '@shared/models/geography.model';
import {
  MapRenderRequest,
  type MapCameraState,
  type MapRenderAiProvider,
} from '@shared/models/design/map-render.model';
import {
  GEMINI_IMAGE_ALLOWED_MODELS,
  GROK_IMAGE_ALLOWED_MODELS,
  OPEN_AI_IMAGE_ALLOWED_MODELS,
  type GeminiImageAllowedModels,
  type GrokImageAllowedModels,
  type OpenAiImageAllowedModels,
} from '@shared/models/ai/ai-models.model';
import { AI_MODEL_USE_CASES } from '@shared/models/ai/ai-model-use-cases.model';
import { assertUnreachable } from '@shared/helpers/function.helpers';
import {
  buildClientControlledSystemPrompt,
  buildDefaultUserPrompt,
} from './design-ai-render-prompt.helpers';

export type GenerateImageSourceType = MapRenderRequest['sourceType'];

export type GenerateImageDialogValue = {
  systemPrompt: string;
  userPrompt: string;
  sourceType: GenerateImageSourceType;
  provider: MapRenderAiProvider;
  model: string;
};

export type GenerateImageDialogDefaults = GenerateImageDialogValue & {
  camera: MapCameraState;
  geminiModel: GeminiImageAllowedModels;
  openAiModel: OpenAiImageAllowedModels;
  grokModel: GrokImageAllowedModels;
};

export type MapCaptureSourcePayload =
  | Pick<
      Extract<MapRenderRequest, { sourceType: 'screenshot' }>,
      'sourceType' | 'screenshot'
    >
  | Pick<
      Extract<MapRenderRequest, { sourceType: 'building-json' }>,
      'sourceType' | 'buildingDesign'
    >
  | Pick<
      Extract<MapRenderRequest, { sourceType: 'screenshot+building-json' }>,
      'sourceType' | 'screenshot' | 'buildingDesign'
    >;

type ViewMode = MapRenderRequest['mode'];

const DEFAULT_GENERATE_IMAGE_PROVIDER: MapRenderAiProvider = 'gemini';
const DEFAULT_GENERATE_IMAGE_SOURCE_TYPE: GenerateImageSourceType =
  'screenshot+building-json';

const MAP_CAPTURE_DOWNLOAD_SOURCE_LABEL: Record<
  GenerateImageSourceType,
  string
> = {
  'building-json': 'json',
  screenshot: 'image',
  'screenshot+building-json': 'image+json',
};

/**
 * Filename fragment for a downloaded map-capture image.
 */
export const getMapCaptureDownloadSourceLabel = (
  sourceType: GenerateImageSourceType,
): string => MAP_CAPTURE_DOWNLOAD_SOURCE_LABEL[sourceType];

/**
 * Whether Generate image can submit. Description is enough for everyone.
 * Admins may submit with only a system prompt (macros are optional).
 */
export const canSubmitGenerateImageDialog = (input: {
  userPrompt: string;
  systemPrompt: string;
  canEditAdvanced: boolean;
}): boolean => {
  if (input.userPrompt.trim()) return true;
  return input.canEditAdvanced && Boolean(input.systemPrompt.trim());
};

/**
 * Allowed models for the Generate image dialog's Model select.
 */
export const generateImageModelsForProvider = (
  provider: MapRenderAiProvider,
): readonly string[] => {
  switch (provider) {
    case 'openai':
      return OPEN_AI_IMAGE_ALLOWED_MODELS;
    case 'gemini':
      return GEMINI_IMAGE_ALLOWED_MODELS;
    case 'grok':
      return GROK_IMAGE_ALLOWED_MODELS;
    default:
      return assertUnreachable(provider);
  }
};

/**
 * Default AI selection when the admin switches provider in the dialog.
 */
export const buildGenerateImageAiForProvider = (
  provider: MapRenderAiProvider,
  defaults: Pick<
    GenerateImageDialogDefaults,
    'geminiModel' | 'openAiModel' | 'grokModel'
  >,
): { provider: MapRenderAiProvider; model: string } => {
  switch (provider) {
    case 'openai':
      return { provider: 'openai', model: defaults.openAiModel };
    case 'gemini':
      return { provider: 'gemini', model: defaults.geminiModel };
    case 'grok':
      return { provider: 'grok', model: defaults.grokModel };
    default:
      return assertUnreachable(provider);
  }
};

/**
 * Default dialog fields for map capture. For the report cover toolbar, pass
 * {@link AI_MODEL_USE_CASES}.reportCoverImageGemini.defaultModel as geminiModel
 * so the admin Models page matches runtime.
 */
export const buildGenerateImageDialogDefaults = (input: {
  camera: MapCameraState;
  mode: ViewMode;
  location: Coordinate | undefined;
  buildingDesign: BuildingDesign;
  geminiModel?: GeminiImageAllowedModels;
  openAiModel?: OpenAiImageAllowedModels;
  grokModel?: GrokImageAllowedModels;
}): GenerateImageDialogDefaults => {
  const geminiModel =
    input.geminiModel ?? AI_MODEL_USE_CASES.mapCameraCaptureGemini.defaultModel;
  const openAiModel =
    input.openAiModel ?? AI_MODEL_USE_CASES.mapCameraCaptureOpenAi.defaultModel;
  const grokModel =
    input.grokModel ?? AI_MODEL_USE_CASES.mapCameraCaptureGrok.defaultModel;
  return {
    provider: DEFAULT_GENERATE_IMAGE_PROVIDER,
    model: geminiModel,
    geminiModel,
    openAiModel,
    grokModel,
    sourceType: DEFAULT_GENERATE_IMAGE_SOURCE_TYPE,
    systemPrompt: buildClientControlledSystemPrompt({
      sourceType: DEFAULT_GENERATE_IMAGE_SOURCE_TYPE,
      mode: input.mode,
      camera: input.camera,
      location: input.location,
      buildingDesign: input.buildingDesign,
    }),
    userPrompt: buildDefaultUserPrompt(),
    camera: input.camera,
  };
};

type BuildMapCaptureSourcePayloadInput = {
  sourceType: GenerateImageSourceType;
  screenshot?: string;
  buildingDesign: BuildingDesign;
};

/**
 * Geometry-only payload for map capture `/design-ai/renderings`.
 * Screenshot is required for image and image+json formats.
 */
export const buildMapCaptureSourcePayload = (
  input: BuildMapCaptureSourcePayloadInput,
): MapCaptureSourcePayload => {
  switch (input.sourceType) {
    case 'building-json':
      return {
        sourceType: 'building-json',
        buildingDesign: input.buildingDesign,
      };
    case 'screenshot': {
      if (!input.screenshot) {
        throw new Error('Screenshot required for image geometry format');
      }
      return { sourceType: 'screenshot', screenshot: input.screenshot };
    }
    case 'screenshot+building-json': {
      if (!input.screenshot) {
        throw new Error('Screenshot required for image + json geometry format');
      }
      return {
        sourceType: 'screenshot+building-json',
        screenshot: input.screenshot,
        buildingDesign: input.buildingDesign,
      };
    }
    default:
      return assertUnreachable(input.sourceType);
  }
};
